import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";
import { FORCED_COLORS_BACKPLATE } from "@/app/layout/forced-colors-backplate";
import { PublicDesktopNav, PublicMobileMenu } from "@/app/layout/public-header-nav";
import { GRADIENT_AUTH_BUTTON_CLASSES, UserMenu } from "@/app/layout/user-menu";
import { buildSignInReturnTo } from "@/app/router/sign-in-return-to";
import { useAuth } from "@/core/auth/auth-provider";
import { persistReturnTo } from "@/core/auth/return-to";
import { Button } from "@/shared/components/ui";

/**
 * Branded header for public unauthenticated surfaces (FR-040): the same
 * approved gradient/height/logo as the authenticated shell header, minus the
 * sidebar navigation toggle. Unauthenticated visitors get a Sign in entry
 * point that starts sign-in directly from every public page, /login included
 * (011-CLMS-241 FR-024); authenticated users who reach a public page (e.g.
 * /home/* from the app footer) keep the shell's profile controls. While the session is still
 * resolving (initial /auth/me) or a sign-out is in flight, the auth slot is a
 * non-actionable placeholder so users never see controls for the wrong state.
 * The legacy header dropdowns (011-CLMS-241 FR-015) are opt-in (`siteNavigation`): the site
 * content pages and the sign-in page show them, centred between the logo and the auth slot on
 * large viewports (Tracker header layout; measured contrast in docs/accessibility-audit.md)
 * and behind a menu button next to the logo on small ones; the focused registration,
 * first-credential and payment flows keep the logo-only header. They are public information links,
 * not the role-gated application navigation.
 */
export function PublicHeader({
  siteNavigation = false,
}: {
  siteNavigation?: boolean;
}): ReactElement {
  const auth = useAuth();
  const location = useLocation();
  // "loading" = initial session fetch; "logout" = sign-out in flight. Both
  // are transitional: rendering Sign in or profile controls here would offer
  // an action for a state that may be wrong a moment later.
  const isTransitional = auth.status === "loading" || auth.status === "logout";
  const isAuthenticated = auth.session?.authenticated === true;
  const signInReturnTo = buildSignInReturnTo(location);

  return (
    <header
      role="banner"
      className="sticky top-0 z-30 bg-gradient-to-r from-[#16456B] to-[#1893D1]"
    >
      <div className="clm-page-gutter mx-auto flex h-[54px] max-w-[1200px] items-center justify-between gap-4 text-white">
        <div className="flex min-w-0 items-center gap-3 self-stretch lg:gap-6">
          {siteNavigation ? <PublicMobileMenu /> : null}
          <Link
            to="/"
            className="flex items-center gap-2 self-center no-underline hover:no-underline"
            aria-label="CLM Web home"
          >
            <img
              src="/logos/header-logo.svg"
              alt="CLM Web logo"
              className={`h-[30px] w-auto ${FORCED_COLORS_BACKPLATE}`}
              loading="eager"
              width={74}
              height={30}
            />
          </Link>
        </div>
        {siteNavigation ? <PublicDesktopNav /> : null}
        {isTransitional ? (
          // Reserves the auth-slot footprint (sm button height) so the header
          // does not shift when the resolved control mounts.
          <span aria-hidden="true" className="inline-block h-8 w-20" />
        ) : isAuthenticated ? (
          <UserMenu />
        ) : (
          // Filled white button matching Sign out (user-menu.tsx) so it reads on the gradient's
          // light right end (Tracker continuity, Luis 2026-10-30; docs/accessibility-audit.md).
          // The underline suppression keeps the asChild anchor identical to the Sign out <button>.
          // A plain anchor: the auth service is a different origin, so the browser leaves the SPA.
          <Button
            asChild
            variant="outline"
            size="sm"
            className={`${GRADIENT_AUTH_BUTTON_CLASSES} no-underline hover:no-underline`}
          >
            <a
              href={auth.client.getLoginUrl(signInReturnTo)}
              onClick={() => persistReturnTo(signInReturnTo)}
            >
              Sign in
            </a>
          </Button>
        )}
      </div>
    </header>
  );
}
